import { MapViewportEventView, MapViewportUnitView } from '@mark43/cad-api';
import { AmlCallView, PhoneCallView } from '../../phone-calls/types';
import { POLYLINES } from '../constants';
import {
    EsriMapEntityTypeEnum,
    GeoPointLocation,
    MapViewportDerivedPolyline,
    TimestampedGeoPoint,
} from '../types';
import { areLatLngsEqual, isValidLatLng } from '../../location/helpers';
import { getEventLocation, getMapViewportUnitLocation, getUnitUserLocation } from './locations';
import { sdbmHash } from './utils';
import { colors } from '../../colors/colors';

export const polylineRenderer = {
    type: 'unique-value',
    field: 'type',
    authoringInfo: undefined,
    defaultSymbol: {
        // used when item field dont match anything defined in uniqueValueInfos
        type: 'simple-line',
        color: colors.esriColors.polylineBorder,
        width: 2,
    },
    uniqueValueInfos: [
        {
            value: POLYLINES.EVENT_TO_UNIT,
            symbol: {
                type: 'simple-line',
                color: colors.esriColors.polylineBorder,
                width: 2,
            },
        },
        {
            value: POLYLINES.UNIT_TO_USER,
            symbol: {
                type: 'simple-line',
                color: colors.esriColors.polylineTracked,
                width: 1,
                style: 'dash',
            },
        },
        {
            // polyline between event and ani/ali phone call (E911)
            value: POLYLINES.EVENT_TO_E911,
            symbol: {
                type: 'simple-line',
                color: colors.esriColors.polylineDefault,
                width: 3,
            },
        },
        {
            // polyline between event and rapidSOS (AML)
            value: POLYLINES.EVENT_TO_AML,
            symbol: {
                type: 'simple-line',
                color: colors.esriColors.polylineDefault,
                width: 3,
            },
        },
        {
            // polyline between ani/ali phone call (E911) and rapidSOS (AML)
            value: POLYLINES.E911_TO_AML,
            symbol: {
                type: 'simple-line',
                style: 'short-dot',
                color: colors.esriColors.polylineDefault,
                width: 3,
            },
        },
        {
            // polyline between geo point and ani/ali phone call (E911)
            value: POLYLINES.GEO_POINT_TO_E911,
            symbol: {
                type: 'simple-line',
                color: colors.esriColors.polylineDefault,
                width: 3,
            },
        },
        {
            // polyline between geo point and rapidSOS (AML)
            value: POLYLINES.GEO_POINT_TO_AML,
            symbol: {
                type: 'simple-line',
                color: colors.esriColors.polylineDefault,
                width: 3,
            },
        },
    ],
};

/** Check to see if polylines are different based on path lat/lngs.
 */
export const isDifferentPolyline = (
    polyline1: MapViewportDerivedPolyline,
    polyline2: MapViewportDerivedPolyline
) => {
    return (
        !areLatLngsEqual(polyline1.path[0], polyline2.path[0]) ||
        !areLatLngsEqual(polyline1.path[1], polyline2.path[1])
    );
};

export function ifPolylinesContainIds(
    ids: (number | string)[],
    polylines: MapViewportDerivedPolyline[]
): boolean {
    for (const id of ids) {
        const idToSearch = typeof id === 'number' ? String(id) : id;

        if (polylines.some((polyline) => polyline.stringKey.split('_').includes(idToSearch))) {
            return true;
        }
    }

    return false;
}

const createPolylineByType = (
    type:
        | 'EVENT_TO_E911'
        | 'EVENT_TO_AML'
        | 'E911_TO_AML'
        | 'GEO_POINT_TO_E911'
        | 'GEO_POINT_TO_AML'
        | 'UNIT_TO_USER'
        | 'EVENT_TO_UNIT',
    stringKey: string,
    startPoint?: TimestampedGeoPoint,
    endPoint?: TimestampedGeoPoint
): MapViewportDerivedPolyline | undefined => {
    if (
        !type ||
        !startPoint ||
        !endPoint ||
        !isValidLatLng(startPoint.latitude, startPoint.longitude) ||
        !isValidLatLng(endPoint.latitude, endPoint.longitude)
    ) {
        return;
    }

    const polylineConfig = {
        stringKey,
        visible: true,
        path: [
            {
                latitude: startPoint.latitude!,
                longitude: startPoint.longitude!,
            },
            {
                latitude: endPoint.latitude!,
                longitude: endPoint.longitude!,
            },
        ],
        geodesic: true,
    };

    switch (type) {
        case POLYLINES.EVENT_TO_E911: {
            return {
                ...polylineConfig,
                type: POLYLINES.EVENT_TO_E911,
                entityId: sdbmHash(`aml2ep:${stringKey}`),
            };
        }
        case POLYLINES.EVENT_TO_AML: {
            return {
                ...polylineConfig,
                type: POLYLINES.EVENT_TO_AML,
                entityId: sdbmHash(`aml2ev:${stringKey}`),
            };
        }
        case POLYLINES.E911_TO_AML: {
            return {
                ...polylineConfig,
                type: POLYLINES.E911_TO_AML,
                entityId: sdbmHash(`aml2ep:${stringKey}`),
            };
        }
        case POLYLINES.GEO_POINT_TO_AML: {
            return {
                ...polylineConfig,
                type: POLYLINES.GEO_POINT_TO_AML,
                entityId: sdbmHash(`gp2aml:${stringKey}`),
            };
        }
        case POLYLINES.GEO_POINT_TO_E911: {
            return {
                ...polylineConfig,
                type: POLYLINES.GEO_POINT_TO_E911,
                entityId: sdbmHash(`gp2ep:${stringKey}`),
            };
        }
        case POLYLINES.EVENT_TO_UNIT: {
            return {
                ...polylineConfig,
                type: POLYLINES.EVENT_TO_UNIT,
                entityId: sdbmHash(`ue:${stringKey}`),
            };
        }
        case POLYLINES.UNIT_TO_USER: {
            return {
                ...polylineConfig,
                type: POLYLINES.UNIT_TO_USER,
                entityId: sdbmHash(`uu:${stringKey}`),
                strokeOpacity: 0,
                icons: [
                    {
                        icon: {
                            path: 'M 0,-1 0,2',
                            strokeOpacity: 0.5,
                            scale: 2,
                            strokeColor: colors.mapPolylineTracked,
                        },
                        offset: '0',
                        repeat: '10px',
                    },
                ],
            };
        }
    }
};

const createEventToPhoneCallsPolylines = (
    selectedEvent?: MapViewportEventView,
    phoneCall?: PhoneCallView,
    amlCall?: AmlCallView
): MapViewportDerivedPolyline[] => {
    const polylines: MapViewportDerivedPolyline[] = [];

    if (selectedEvent && !selectedEvent.visible) {
        return polylines;
    }
    const selectedEventLocation = getEventLocation(selectedEvent);

    const isPhoneCallAvailable = isValidLatLng(
        phoneCall?.normalizedPhoneCallDisplay?.latitude,
        phoneCall?.normalizedPhoneCallDisplay?.longitude
    );
    const isAmlCallAvailable = isValidLatLng(
        amlCall?.normalizedPhoneCallDisplay?.latFloat,
        amlCall?.normalizedPhoneCallDisplay?.lngFloat
    );

    if (isPhoneCallAvailable) {
        const visiblePhoneCallPolyline = createPolylineByType(
            POLYLINES.EVENT_TO_E911,
            `${selectedEvent?.entityId}_${phoneCall?.id}`,
            selectedEventLocation,
            {
                latitude: phoneCall?.normalizedPhoneCallDisplay?.latFloat,
                longitude: phoneCall?.normalizedPhoneCallDisplay?.lngFloat,
            }
        );

        if (visiblePhoneCallPolyline) {
            polylines.push(visiblePhoneCallPolyline);
        }
    }
    if (isAmlCallAvailable) {
        const visibleAmlCallPolyline = createPolylineByType(
            POLYLINES.EVENT_TO_AML,
            `${selectedEvent?.entityId}_${amlCall?.id}`,
            selectedEventLocation,
            {
                latitude: amlCall?.normalizedPhoneCallDisplay.latFloat,
                longitude: amlCall?.normalizedPhoneCallDisplay.lngFloat,
            }
        );

        if (visibleAmlCallPolyline) {
            polylines.push(visibleAmlCallPolyline);
        }
    }
    if (isAmlCallAvailable && isPhoneCallAvailable) {
        const visibleAmlToPhoneCallPolyline = createPolylineByType(
            POLYLINES.E911_TO_AML,
            `${phoneCall?.id}_${amlCall?.id}`,
            {
                latitude: phoneCall?.normalizedPhoneCallDisplay?.latFloat,
                longitude: phoneCall?.normalizedPhoneCallDisplay?.lngFloat,
            },
            {
                latitude: amlCall?.normalizedPhoneCallDisplay.latFloat,
                longitude: amlCall?.normalizedPhoneCallDisplay.lngFloat,
            }
        );

        if (visibleAmlToPhoneCallPolyline) {
            polylines.push(visibleAmlToPhoneCallPolyline);
        }
    }
    return polylines;
};

const createEventToUnitsPolylines = ({
    mapViewportUnitsById,
    centralEvent,
    showUnitLocationWhenGpsIsNotAvailable,
}: {
    mapViewportUnitsById: Record<number, MapViewportUnitView>;
    centralEvent?: MapViewportEventView;
    showUnitLocationWhenGpsIsNotAvailable?: boolean;
}): MapViewportDerivedPolyline[] => {
    const polylines: MapViewportDerivedPolyline[] = [];

    // TODO: MAP RND-11325
    if (!centralEvent?.visible) {
        return polylines;
    }

    if (centralEvent?.assignedUnitIds) {
        for (const unitId of centralEvent.assignedUnitIds) {
            const mapUnit = mapViewportUnitsById[unitId];
            const mapUnitLocation = getMapViewportUnitLocation(
                mapUnit,
                showUnitLocationWhenGpsIsNotAvailable
            );

            // TODO: MAP RND-11325
            if (mapUnit?.visible && mapUnitLocation) {
                const unitEventPolyline = createPolylineByType(
                    POLYLINES.EVENT_TO_UNIT,
                    `${unitId}_${centralEvent.entityId}`,
                    mapUnitLocation,
                    getEventLocation(centralEvent)
                );

                if (unitEventPolyline) {
                    polylines.push(unitEventPolyline);
                }
            }
        }
    }

    return polylines;
};

const createPolylinesForGeoPoint = (
    phoneCall?: PhoneCallView,
    amlCall?: AmlCallView,
    activeGeoPoint?: GeoPointLocation
): MapViewportDerivedPolyline[] => {
    const polylines: MapViewportDerivedPolyline[] = [];

    if (!activeGeoPoint) return polylines;

    const isPhoneCallAvailable = isValidLatLng(
        phoneCall?.normalizedPhoneCallDisplay?.latitude,
        phoneCall?.normalizedPhoneCallDisplay?.longitude
    );
    const isAmlCallAvailable = isValidLatLng(
        amlCall?.normalizedPhoneCallDisplay?.latFloat,
        amlCall?.normalizedPhoneCallDisplay?.lngFloat
    );
    const startPoint = {
        latitude: activeGeoPoint?.latitude,
        longitude: activeGeoPoint?.longitude,
        entityId: activeGeoPoint?.entityId,
    };

    if (isPhoneCallAvailable) {
        const phoneCallPolyline = createPolylineByType(
            POLYLINES.GEO_POINT_TO_E911,
            `${activeGeoPoint?.entityId}_${phoneCall?.id}`,
            startPoint,
            {
                latitude: phoneCall?.normalizedPhoneCallDisplay?.latFloat,
                longitude: phoneCall?.normalizedPhoneCallDisplay?.lngFloat,
            }
        );

        if (phoneCallPolyline) {
            polylines.push(phoneCallPolyline);
        }
    }
    if (isAmlCallAvailable) {
        const amlCallPolyline = createPolylineByType(
            POLYLINES.GEO_POINT_TO_AML,
            `${activeGeoPoint?.entityId}_${amlCall?.id}`,
            startPoint,
            {
                latitude: amlCall?.normalizedPhoneCallDisplay.latFloat,
                longitude: amlCall?.normalizedPhoneCallDisplay.lngFloat,
            }
        );

        if (amlCallPolyline) {
            polylines.push(amlCallPolyline);
        }
    }
    if (isAmlCallAvailable && isPhoneCallAvailable) {
        const visibleAmlToPhoneCallPolyline = createPolylineByType(
            POLYLINES.E911_TO_AML,
            `${phoneCall?.id}_${amlCall?.id}`,
            {
                latitude: phoneCall?.normalizedPhoneCallDisplay?.latFloat,
                longitude: phoneCall?.normalizedPhoneCallDisplay?.lngFloat,
            },
            {
                latitude: amlCall?.normalizedPhoneCallDisplay.latFloat,
                longitude: amlCall?.normalizedPhoneCallDisplay.lngFloat,
            }
        );

        if (visibleAmlToPhoneCallPolyline) {
            polylines.push(visibleAmlToPhoneCallPolyline);
        }
    }

    return polylines;
};

const createPolylinesForSelectedUnit = ({
    selectedUnit,
    showUnitLocationWhenGpsIsNotAvailable,
}: {
    selectedUnit?: MapViewportUnitView;
    showUnitLocationWhenGpsIsNotAvailable?: boolean;
}) => {
    const polylines: MapViewportDerivedPolyline[] = [];
    const selectedUnitLocation = getMapViewportUnitLocation(
        selectedUnit,
        showUnitLocationWhenGpsIsNotAvailable
    );

    if (
        !selectedUnit ||
        // TODO: MAP RND-11325
        !selectedUnit.visible ||
        !selectedUnit.unitUserLocations?.length ||
        !selectedUnitLocation ||
        !isValidLatLng(selectedUnitLocation?.latitude, selectedUnitLocation?.longitude)
    ) {
        return polylines;
    }
    // Draw polylines between selected units / users
    selectedUnit.unitUserLocations.forEach((unitUserLocation) => {
        const unitUserGpsLocation = getUnitUserLocation(unitUserLocation);

        if (!unitUserGpsLocation) {
            return;
        }

        const unitUserPolyline = createPolylineByType(
            POLYLINES.UNIT_TO_USER,
            `${selectedUnit.entityId}_${
                unitUserGpsLocation.entityId ||
                // unitUserGpsLocation should have entityId, fallback to use lat lng in case entityId is missing
                `${unitUserGpsLocation.latitude}_${unitUserGpsLocation.longitude}`
            }`,
            selectedUnitLocation,
            unitUserGpsLocation
        );
        if (unitUserPolyline) {
            polylines.push(unitUserPolyline);
        }
    });

    return polylines;
};

type CreatePolylinesParams = {
    mapViewportUnitsById: Record<number, MapViewportUnitView>;
    showMobileDevicesPolylinesOnMap: boolean;
    centralEvent?: MapViewportEventView;
    assignedEvent?: MapViewportEventView;
    activeGeoPoint?: GeoPointLocation;
    selectedUnit?: MapViewportUnitView;
    phoneCall?: PhoneCallView;
    amlCall?: AmlCallView;
    entityType?: EsriMapEntityTypeEnum;
    showUnitLocationWhenGpsIsNotAvailable?: boolean;
    amlUser: boolean;
};

export const createPolylines = ({
    entityType,
    phoneCall,
    amlCall,
    selectedUnit,
    centralEvent,
    assignedEvent,
    activeGeoPoint,
    showMobileDevicesPolylinesOnMap,
    showUnitLocationWhenGpsIsNotAvailable,
    mapViewportUnitsById,
    amlUser,
}: CreatePolylinesParams): MapViewportDerivedPolyline[] => {
    switch (entityType) {
        case EsriMapEntityTypeEnum.PHONE_CALL:
        case EsriMapEntityTypeEnum.AML_CALL: {
            const eventToUnits = createEventToUnitsPolylines({
                mapViewportUnitsById,
                centralEvent: centralEvent || assignedEvent,
                showUnitLocationWhenGpsIsNotAvailable,
            });
            const eventToPhoneCalls = amlUser
                ? createEventToPhoneCallsPolylines(
                      centralEvent || assignedEvent,
                      phoneCall,
                      amlCall
                  )
                : [];

            const geoPointToPhoneCalls = createPolylinesForGeoPoint(
                phoneCall,
                amlCall,
                activeGeoPoint
            );

            return [...eventToUnits, ...eventToPhoneCalls, ...geoPointToPhoneCalls];
        }
        case EsriMapEntityTypeEnum.EVENT: {
            const eventToUnits = createEventToUnitsPolylines({
                mapViewportUnitsById,
                centralEvent: centralEvent || assignedEvent,
                showUnitLocationWhenGpsIsNotAvailable,
            });
            const eventToPhoneCalls = amlUser
                ? createEventToPhoneCallsPolylines(
                      centralEvent || assignedEvent,
                      phoneCall,
                      amlCall
                  )
                : [];

            return [...eventToUnits, ...eventToPhoneCalls];
        }
        case EsriMapEntityTypeEnum.UNIT:
        case EsriMapEntityTypeEnum.USER: {
            if (!showMobileDevicesPolylinesOnMap) return [];

            const unitToEvent = createEventToUnitsPolylines({
                mapViewportUnitsById,
                centralEvent: assignedEvent,
                showUnitLocationWhenGpsIsNotAvailable,
            });

            const unitToUsers = createPolylinesForSelectedUnit({
                selectedUnit,
                showUnitLocationWhenGpsIsNotAvailable,
            });
            const eventToPhoneCalls = amlUser
                ? createEventToPhoneCallsPolylines(assignedEvent, phoneCall, amlCall)
                : [];

            return [...unitToEvent, ...unitToUsers, ...eventToPhoneCalls];
        }
        case EsriMapEntityTypeEnum.GEO_POINT_LOCATION: {
            const geoPointToPhoneCalls = createPolylinesForGeoPoint(
                phoneCall,
                amlCall,
                activeGeoPoint
            );

            return [...geoPointToPhoneCalls];
        }
        default: {
            return [];
        }
    }
};
